import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const StudentClassNotes = ({ onNavigate, userInfo }) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ subjects: [], batches: [] });
    const [studentInfo, setStudentInfo] = useState({});
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedBatch, setSelectedBatch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    useEffect(() => { fetchNotes(); }, []);

    const fetchNotes = async (subject = '', batch = '') => {
        try {
            setLoading(true);
            let url = `${API_LINK}/api/student/class-notes`;
            const params = [];
            if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
            if (batch) params.push(`batch=${encodeURIComponent(batch)}`);
            if (params.length > 0) url += '?' + params.join('&');

            const res = await fetch(url, { headers });
            const r = await res.json();
            if (r.success) {
                setNotes(r.data?.notes || []);
                setFilters(r.data?.filters || { subjects: [], batches: [] });
                setStudentInfo(r.data?.studentInfo || {});
            }
        } catch (e) { console.error('Error fetching notes:', e); }
        finally { setLoading(false); }
    };

    const handleFilterChange = (type, value) => {
        if (type === 'subject') {
            setSelectedSubject(value);
            fetchNotes(value, selectedBatch);
        } else {
            setSelectedBatch(value);
            fetchNotes(selectedSubject, value);
        }
    };

    const handleDownload = async (note) => {
        try {
            // Track download
            await fetch(`${API_LINK}/api/student/class-notes/${note.id}/download`, {
                method: 'POST', headers
            });
            // Open file
            window.open(`${API_LINK}/${note.file_path || ''}`, '_blank');
            // Refresh to update count
            fetchNotes(selectedSubject, selectedBatch);
        } catch (e) { console.error('Download error:', e); }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return '📄';
        const ext = fileName.split('.').pop().toLowerCase();
        const icons = { pdf: '📕', doc: '📘', docx: '📘', ppt: '📙', pptx: '📙', xls: '📗', xlsx: '📗', txt: '📝', zip: '📦', rar: '📦', jpg: '🖼️', jpeg: '🖼️', png: '🖼️' };
        return icons[ext] || '📄';
    };

    const filteredNotes = notes.filter(n =>
        !searchTerm || n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.teacher_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const cs = {
        card: { background: '#fff', borderRadius: '14px', padding: '1.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', transition: 'all 0.2s' },
        btn: { padding: '0.6rem 1.2rem', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s' },
        input: { width: '100%', padding: '0.7rem 1rem', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' },
        badge: { padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600', display: 'inline-block' }
    };

    return (
        <Layout role="student" onNavigate={onNavigate} activePage="student-class-notes" userInfo={userInfo}>
            {/* Header */}
            <div style={{ marginBottom: '1.5rem' }}>
                <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '700', color: '#1e293b' }}>📒 Class Notes</h1>
                <p style={{ margin: '0.3rem 0 0', color: '#64748b', fontSize: '0.95rem' }}>
                    Download class notes uploaded by your teachers
                    {studentInfo.batch && <span> • <strong>{studentInfo.batch}</strong></span>}
                    {studentInfo.department && <span> • {studentInfo.department}</span>}
                </p>
            </div>

            {/* Filters & Search */}
            <div style={{ ...cs.card, marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Search */}
                <div style={{ flex: '2', minWidth: '200px' }}>
                    <input
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder="🔍 Search by title, subject, or teacher..."
                        style={{ ...cs.input, border: '2px solid #e2e8f0' }}
                    />
                </div>

                {/* Subject Filter */}
                <div style={{ flex: '1', minWidth: '160px' }}>
                    <select value={selectedSubject}
                        onChange={e => handleFilterChange('subject', e.target.value)}
                        style={cs.input}>
                        <option value="">All Subjects</option>
                        {filters.subjects.map((s, i) => (
                            <option key={i} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                {/* Batch Filter */}
                <div style={{ flex: '1', minWidth: '160px' }}>
                    <select value={selectedBatch}
                        onChange={e => handleFilterChange('batch', e.target.value)}
                        style={cs.input}>
                        <option value="">My Batch ({studentInfo.batch || '...'})</option>
                        {filters.batches.map((b, i) => (
                            <option key={i} value={b}>{b}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                {[
                    { label: 'Available Notes', value: notes.length, icon: '📒', bg: '#eff6ff', color: '#2563eb' },
                    { label: 'Subjects', value: filters.subjects.length, icon: '📖', bg: '#f0fdf4', color: '#16a34a' },
                    { label: 'Total Downloads', value: notes.reduce((a, n) => a + (n.download_count || 0), 0), icon: '⬇️', bg: '#faf5ff', color: '#9333ea' }
                ].map((s, i) => (
                    <div key={i} style={{ ...cs.card, background: s.bg, borderLeft: `4px solid ${s.color}`, padding: '1rem 1.2rem' }}>
                        <div style={{ fontSize: '1.4rem', marginBottom: '0.2rem' }}>{s.icon}</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: '700', color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: '500' }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Notes List */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    <div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTop: '4px solid #238548', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
                    Loading class notes...
                </div>
            ) : filteredNotes.length === 0 ? (
                <div style={{ ...cs.card, textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <h3 style={{ margin: 0, color: '#475569' }}>{searchTerm ? 'No matching notes found' : 'No Class Notes Available'}</h3>
                    <p style={{ margin: '0.5rem 0 0' }}>
                        {searchTerm ? 'Try a different search term or change filters' : 'No class notes have been uploaded for your batch yet. Check back later!'}
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    {filteredNotes.map(note => (
                        <div key={note.id} style={{ ...cs.card, display: 'flex', alignItems: 'center', gap: '1.2rem', flexWrap: 'wrap', cursor: 'pointer' }}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(35,133,72,0.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            onClick={() => handleDownload(note)}>

                            {/* File Icon */}
                            <div style={{
                                width: 55, height: 55, borderRadius: '14px',
                                background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.8rem', flexShrink: 0
                            }}>
                                {getFileIcon(note.file_name)}
                            </div>

                            {/* Note Info */}
                            <div style={{ flex: 1, minWidth: '200px' }}>
                                <div style={{ fontWeight: '600', fontSize: '1.05rem', color: '#1e293b', marginBottom: '0.3rem' }}>
                                    {note.title}
                                </div>
                                {note.description && (
                                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.4rem', lineHeight: 1.4 }}>
                                        {note.description}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                    <span style={{ ...cs.badge, background: '#dbeafe', color: '#2563eb' }}>📖 {note.subject}</span>
                                    <span style={{ ...cs.badge, background: '#dcfce7', color: '#16a34a' }}>👥 {note.batch_name}</span>
                                    <span style={{ ...cs.badge, background: '#f3e8ff', color: '#7c3aed' }}>👨‍🏫 {note.teacher_name}</span>
                                </div>
                            </div>

                            {/* Meta + Download */}
                            <div style={{ textAlign: 'right', minWidth: '130px', flexShrink: 0 }}>
                                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.3rem' }}>
                                    {formatFileSize(note.file_size)} • {note.download_count || 0} downloads
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                                    {new Date(note.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                                <button
                                    onClick={e => { e.stopPropagation(); handleDownload(note); }}
                                    style={{
                                        ...cs.btn,
                                        background: 'linear-gradient(135deg, #238548, #16a34a)',
                                        color: '#fff', fontSize: '0.85rem',
                                        padding: '0.5rem 1rem',
                                        boxShadow: '0 3px 10px rgba(35,133,72,0.25)'
                                    }}>
                                    ⬇️ Download
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </Layout>
    );
};

export default StudentClassNotes;
